import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import { Box, Button, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CHAT_MENTION_ALL_ID,
  desktopReleaseAnnouncementVersion,
  isSystemChatMessage,
  segmentChatText,
  type ChatMessage,
} from "@xop/dashboard-shared";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { isTauri } from "../../../lib/notifications/desktopBridge.js";
import { ProfileCardTrigger } from "../../../components/ProfileCardTrigger.js";
import { ChatAttachmentView } from "../ChatAttachmentView.js";
import { dayDividerLabel, messageTime, sameDay } from "../format.js";
import { DeliveryStatus } from "./DeliveryStatus.js";
import { ReactionBar } from "./ReactionBar.js";
import { UpdateNowButton } from "./UpdateNowButton.js";

const msgActionBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  height: 18,
  width: 18,
  borderRadius: 0.75,
  border: 0,
  bgcolor: "transparent",
  color: "chat.meta",
  cursor: "pointer",
  "&:hover": { color: "chat.accent" },
} as const;

/**
 * One message in the thread: the day-divider (when the calendar day changes),
 * the bubble (sender name / bot badge, deleted tombstone, inline edit field, or
 * the segmented body + attachment + desktop "Update now"), the time row with
 * the own-message edit/delete actions + DM ticks, the floating reaction pill,
 * and — under my latest DM message — the explicit Sent/Delivered/Seen caption.
 *
 * Presentational: the one-at-a-time edit + delete state lives in `ChatThread`
 * and is passed in (`editing` / `editText` / the `on*` callbacks).
 */
export function MessageBubble({
  message: m,
  prev,
  selfId,
  showSenderNames,
  isDm,
  readUpTo,
  recipientOnline,
  myLastDmId,
  editing,
  editText,
  onEditTextChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onRequestDelete,
  canReact,
  canEdit,
  canDelete,
  canReply,
  highlighted,
  onReact,
  onReply,
  onJumpToMessage,
}: {
  message: ChatMessage;
  prev: ChatMessage | undefined;
  selfId: number;
  showSenderNames: boolean;
  isDm: boolean;
  readUpTo: number;
  recipientOnline: boolean;
  myLastDmId: number;
  editing: boolean;
  editText: string;
  onEditTextChange: (v: string) => void;
  onStartEdit: (message: ChatMessage) => void;
  onCancelEdit: () => void;
  onSaveEdit: (messageId: number, body: string) => void;
  onRequestDelete: (messageId: number) => void;
  canReact: boolean;
  canEdit: boolean;
  canDelete: boolean;
  /** Show the Reply affordance (any message, mine or not — WhatsApp-style). */
  canReply: boolean;
  /** Transiently highlighted because a reply quote just jumped to it. */
  highlighted: boolean;
  onReact: (messageId: number, emoji: string) => void;
  onReply: (message: ChatMessage) => void;
  /** Scroll to + flash the quoted original (no-op if it's paged out). */
  onJumpToMessage: (messageId: number) => void;
}): JSX.Element {
  const dayChanged = !prev || !sameDay(prev.created_at, m.created_at);
  const mine = m.sender_id === selfId;
  // Automated "XOP Updates" bot post (e.g. a new-version announcement) —
  // rendered with a BOT badge + the robot mark, never as a person. It is
  // never "mine" (reserved sender id 0 ≠ any real user).
  const isSystem = isSystemChatMessage(m);
  // The automated "XOP Updates" release post carries an inline "Update
  // now" action on the DESKTOP app — it fires the exact same flow as the
  // tray "Check for Updates…" item. On the web (no updater) the post
  // stays text-only.
  const updateVersion = isSystem ? desktopReleaseAnnouncementVersion(m) : null;
  const newGroup = dayChanged || !prev || prev.sender_id !== m.sender_id;
  const read = isDm && mine && m.id <= readUpTo;

  return (
    <Box>
      {dayChanged && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
          <Typography
            sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 1.5,
              bgcolor: "chat.headerBg",
              color: "chat.meta",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              boxShadow: 1,
            }}
          >
            {dayDividerLabel(m.created_at)}
          </Typography>
        </Box>
      )}
      <Box
        data-testid="chat-message"
        data-message-id={m.id}
        sx={{
          display: "flex",
          justifyContent: mine ? "flex-end" : "flex-start",
          mt: newGroup ? 0.75 : 0.15,
          // Reactions hang off the bubble's bottom edge (absolute), so a
          // reacted message needs clearance below it for the floating pill.
          mb: (m.reactions?.length ?? 0) > 0 && !m.deleted_at ? 2 : 0,
        }}
      >
        <Box
          sx={{
            position: "relative", // anchor for the edge-hung reaction pill
            display: "flex",
            flexDirection: "column",
            maxWidth: { xs: "85%", sm: "65%" },
            pl: 1.25,
            pr: 1.25,
            pt: newGroup && !mine && showSenderNames ? 0.5 : 0.6,
            pb: 0.5,
            bgcolor: mine ? "chat.bubbleOut" : "chat.bubbleIn",
            color: mine ? "chat.bubbleOutText" : "chat.bubbleInText",
            boxShadow: 1,
            // Reveal the edit/delete + add-reaction affordances on
            // hover/focus (they stay in the DOM + keyboard-reachable, so
            // tests can find them).
            "&:hover .chat-msg-actions, &:focus-within .chat-msg-actions": { opacity: 1 },
            "&:hover .chat-react-add, &:focus-within .chat-react-add": { opacity: 1 },
            // WhatsApp-style asymmetric corner on the first bubble of a run.
            borderRadius: 1.5,
            ...(newGroup
              ? mine
                ? { borderTopRightRadius: 2 }
                : { borderTopLeftRadius: 2 }
              : {}),
            // A system/bot post gets a subtle left accent so it reads as
            // an automated notice, not a teammate's message.
            ...(isSystem ? { borderLeft: 3, borderColor: "chat.accent" } : {}),
            // Transient flash when a reply quote jumps here. The outline is
            // always reserved (transparent) so highlighting never shifts layout.
            outline: "2px solid",
            outlineColor: highlighted ? "chat.accent" : "transparent",
            transition: "outline-color 250ms ease",
          }}
        >
          {newGroup && !mine && showSenderNames && isSystem && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
              <SmartToyRoundedIcon sx={{ fontSize: 15, color: "chat.accent" }} aria-hidden />
              <Typography
                component="span"
                sx={{ fontSize: 12.5, fontWeight: 700, color: "chat.accent" }}
              >
                {m.sender_name}
              </Typography>
              <Box
                component="span"
                data-testid="chat-bot-badge"
                sx={{
                  px: 0.5,
                  py: 0.05,
                  borderRadius: 0.75,
                  bgcolor: "chat.accent",
                  color: "chat.bubbleIn",
                  fontSize: 9.5,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Bot
              </Box>
            </Box>
          )}
          {newGroup && !mine && showSenderNames && !isSystem && (
            <Box sx={{ mb: 0.25 }}>
              <ProfileCardTrigger userId={m.sender_id} name={m.sender_name}>
                <Typography
                  component="span"
                  sx={{ fontSize: 12.5, fontWeight: 700, color: "chat.accent" }}
                >
                  {m.sender_name}
                </Typography>
              </ProfileCardTrigger>
            </Box>
          )}
          {m.deleted_at ? (
            <Typography
              data-testid="chat-message-deleted"
              sx={{ fontSize: 13.5, fontStyle: "italic", color: "chat.meta", display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
              This message was deleted
            </Typography>
          ) : editing ? (
            <Box sx={{ display: "grid", gap: 0.75, minWidth: { xs: 200, sm: 280 } }}>
              <TextField
                multiline
                size="small"
                autoFocus
                value={editText}
                onChange={(e) => onEditTextChange(e.target.value)}
                inputProps={{ "aria-label": "Edit message" }}
                sx={{ "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button size="small" variant="text" color="inherit" onClick={onCancelEdit}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={editText.trim().length === 0}
                  onClick={() => onSaveEdit(m.id, editText.trim())}
                >
                  Save
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {m.reply_to && (
                <Box
                  component="button"
                  type="button"
                  data-testid="chat-reply-quote"
                  onClick={() => onJumpToMessage(m.reply_to!.id)}
                  sx={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    border: 0,
                    cursor: "pointer",
                    mb: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    borderLeft: 3,
                    borderColor: "chat.accent",
                    bgcolor: (t) => alpha(t.palette.chat.accent, 0.12),
                    "&:hover": { bgcolor: (t) => alpha(t.palette.chat.accent, 0.2) },
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ display: "block", fontSize: 12, fontWeight: 700, color: "chat.accent" }}
                  >
                    {m.reply_to.sender_id === selfId ? "You" : m.reply_to.sender_name}
                  </Typography>
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      display: "block",
                      fontSize: 12.5,
                      color: "chat.meta",
                      ...(m.reply_to.deleted ? { fontStyle: "italic" } : {}),
                    }}
                  >
                    {m.reply_to.deleted ? "Deleted message" : m.reply_to.preview}
                  </Typography>
                </Box>
              )}
              {m.attachment && <ChatAttachmentView attachment={m.attachment} mine={mine} />}
              {m.body && (
                <Box
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.35,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {segmentChatText(m.body, m.mentions).map((seg, i) => {
                    // An @teammate chip. The one matching the viewer is accented
                    // harder ("you were mentioned") — and `@channel` counts as
                    // the viewer whoever they are, because it notified them too.
                    const isSelfMention =
                      seg.type === "mention" &&
                      (seg.userId === selfId || seg.userId === CHAT_MENTION_ALL_ID);
                    return seg.type === "mention" ? (
                      <Box
                        key={i}
                        component="span"
                        data-testid="chat-mention"
                        data-self={isSelfMention ? "true" : undefined}
                        sx={{
                          fontWeight: 600,
                          borderRadius: 0.75,
                          px: 0.5,
                          mx: "-2px",
                          color: isSelfMention ? "primary.main" : "info.main",
                          bgcolor: isSelfMention ? "surfaceSoft" : "transparent",
                        }}
                      >
                        {seg.value}
                      </Box>
                    ) : seg.type === "link" ? (
                      <Box
                        key={i}
                        component="a"
                        href={seg.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: "info.main", textDecoration: "underline", wordBreak: "break-all" }}
                      >
                        {seg.value}
                      </Box>
                    ) : seg.type === "applink" ? (
                      // Internal entity deep link (shared lead / prospect /
                      // customer): in-app navigation, NOT a new tab — so it
                      // opens inside the SPA on web and in the desktop app.
                      <Box
                        key={i}
                        component={RouterLink}
                        to={seg.value}
                        data-testid="chat-applink"
                        sx={{ color: "info.main", textDecoration: "underline", wordBreak: "break-all" }}
                      >
                        {seg.value}
                      </Box>
                    ) : (
                      <span key={i}>{seg.value}</span>
                    );
                  })}
                </Box>
              )}
              {updateVersion && isTauri() && <UpdateNowButton version={updateVersion} />}
            </>
          )}

          {/* Time row (+ "edited" + own edit/delete actions). Hidden while
            * editing; a tombstone keeps only the time. */}
          {!editing && (
            <Box
              sx={{
                alignSelf: "flex-end",
                mt: 0.25,
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
              }}
            >
              {!m.deleted_at && (canReply || (mine && (canEdit || canDelete))) && (
                <Box
                  className="chat-msg-actions"
                  sx={{ display: "inline-flex", gap: 0.25, mr: 0.25, opacity: 0, transition: "opacity 150ms" }}
                >
                  {canReply && (
                    <Box
                      component="button"
                      type="button"
                      aria-label="Reply to message"
                      onClick={() => onReply(m)}
                      sx={msgActionBtnSx}
                    >
                      <ReplyRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                  {mine && canEdit && (
                    <Box
                      component="button"
                      type="button"
                      aria-label="Edit message"
                      onClick={() => onStartEdit(m)}
                      sx={msgActionBtnSx}
                    >
                      <EditRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                  {mine && canDelete && (
                    <Box
                      component="button"
                      type="button"
                      aria-label="Delete message"
                      onClick={() => onRequestDelete(m.id)}
                      sx={msgActionBtnSx}
                    >
                      <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
              )}
              {m.edited_at && !m.deleted_at && (
                <Typography component="span" sx={{ fontSize: 10.5, color: "chat.meta", fontStyle: "italic" }}>
                  edited
                </Typography>
              )}
              <Typography component="span" sx={{ fontSize: 10.5, color: "chat.meta" }}>
                {messageTime(m.created_at)}
              </Typography>
              {isDm && mine && !m.deleted_at && (
                read ? (
                  <DoneAllRoundedIcon sx={{ fontSize: 15, color: "chat.tickRead" }} />
                ) : (
                  <DoneRoundedIcon sx={{ fontSize: 15, color: "chat.tick" }} />
                )
              )}
            </Box>
          )}
          {canReact && !m.deleted_at && !editing && (
            <ReactionBar
              reactions={m.reactions ?? []}
              selfId={selfId}
              mine={mine}
              onReact={(emoji) => onReact(m.id, emoji)}
            />
          )}
        </Box>
      </Box>
      {/* Explicit delivery state under MY most-recent DM message — the
        * unmistakable "did they see it" cue beside the subtle tick. */}
      {isDm && mine && !m.deleted_at && m.id === myLastDmId && (
        <DeliveryStatus read={m.id <= readUpTo} recipientOnline={recipientOnline} />
      )}
    </Box>
  );
}
